import * as React from 'react';
import { useCallback, useState, useEffect } from 'react';

import { Viewport, ViewSmall, ViewMedium } from 'bernie-view';

import { UitkCardAloha, UitkCardAlohaContentSection } from 'uitk-react-cards';
import { UitkSheet } from 'uitk-react-sheet';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkHeading, UitkText } from 'uitk-react-text';
import { classNames } from 'uitk-react-utils';
import { UitkLayoutFlexItem } from 'uitk-react-layout-flex';

import { trackAnalytics } from 'utility';
import { useStores } from 'utility/hooks';

import { BaggageFeesInformation } from 'components/search/listings/offer/details';
import { InfoHeader, SummaryAndInfo } from 'components/shared/flight-information';
import { DetailsAndFaresFooter, FareTypesCarousel } from '.';
import { ChangeCancellationMessages } from 'components/search/listings/offer/details/changes-cancellations-fee';
import { FlightAction, FlightsShoppingPwaFlightInformationResults, FlightsStandardOffer } from '__generated__/api';
import Messages = FlightsShoppingPwaFlightInformationResults.Messages;
import { Phrase } from 'components/shared';
import { HygieneAmenities } from './hygiene-amenities';

interface DetailsAndFaresProps {
  showSummaryAndDetails: boolean;
  useOwnFooter: boolean;
  openForcedChoiceModal?: boolean;
  onApplySelect?: (identifier: string) => void;
  handleOfferSelect?: (selectOfferAction: FlightAction.Fragment) => void;
  itemIndex?: number;
  setSelected: (index: number) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  availableFaresInformation: any;
  isSmallViewport: boolean;
  hygieneAmenitiesPresentation?: FlightsStandardOffer.HygieneAmenitiesPresentation;
}

export const DetailsAndFares = ({
  showSummaryAndDetails,
  useOwnFooter,
  openForcedChoiceModal,
  onApplySelect,
  handleOfferSelect,
  itemIndex,
  setSelected,
  availableFaresInformation,
  isSmallViewport,
  hygieneAmenitiesPresentation
}: DetailsAndFaresProps) => {
  const { flightsSelectedJourneyReviewStore, analytics } = useStores();
  const fareChoiceInformation = availableFaresInformation ? availableFaresInformation.fareChoiceInformation : null;
  const flightsJourneyPolicies = availableFaresInformation ? availableFaresInformation.flightsJourneyPolicies : null;
  const scrollAnalytics = fareChoiceInformation ? fareChoiceInformation.scrollAnalytics : null;
  const heading = fareChoiceInformation ? fareChoiceInformation.heading : null;
  const message = fareChoiceInformation ? fareChoiceInformation.message : null;
  const displayAnalytics = fareChoiceInformation ? fareChoiceInformation.displayAnalytics : null;
  const fareTypes = fareChoiceInformation ? fareChoiceInformation.fareTypes : null;
  const hideFareChoiceDetails = fareChoiceInformation ? fareChoiceInformation.hideFareChoiceDetails : null;
  const selectedFareType = fareTypes ? fareTypes.find(fare => fare.selected) : null;
  const [baggageFeesInformation, setBaggageInformation] = useState(null);
  const [changeCancellationMessages, setChangeCancellationMessages] = useState(null);
  const cardClassNames = classNames({
    's-b-margin-twelve s-b-padding-twelve m-b-margin-twelve m-b-padding-twelve': useOwnFooter
  });

  const onFareTypeSelect = useCallback(
    (index: number) => {
      if (fareTypes) {
        const fare = fareTypes[index];
        setBaggageInformation(fare.baggageFeesInformation);
        setChangeCancellationMessages(fare.changeCancellationMessages);
        setSelected(index);
      }
    },
    [fareTypes, setSelected]
  );

  if (showSummaryAndDetails) {
    const flightsSelectedJourneyReview = flightsSelectedJourneyReviewStore.getJourneyReviewItem(
      availableFaresInformation
    );
    flightsSelectedJourneyReviewStore.hydrate({ flightsSelectedJourneyReview });
  }

  useEffect(() => {
    trackAnalytics(displayAnalytics, analytics);
  }, [displayAnalytics, analytics]);

  return (
    <>
      <UitkCardAloha data-test-id="details-and-fares" className={cardClassNames}>
        <InfoHeader itemIndex={itemIndex} />
        {showSummaryAndDetails && (
          <UitkSpacing margin={{ block: 'three' }} padding={{ inline: 'three' }}>
            <UitkCardAlohaContentSection padded={false}>
              <SummaryAndInfo itemIndex={itemIndex} />
            </UitkCardAlohaContentSection>
          </UitkSpacing>
        )}
        {availableFaresInformation && availableFaresInformation.fareChoiceInformation && (
          <>
            <UitkSpacing padding="six">
              <UitkCardAlohaContentSection border="top" padded={false}>
                <UitkHeading level={2} size={500}>
                  <Viewport>
                    <ViewSmall>{heading.shortMessage}</ViewSmall>
                    <ViewMedium>{heading.longMessage}</ViewMedium>
                  </Viewport>
                </UitkHeading>
                <UitkText spacing="two" size={300}>
                  {message}
                </UitkText>
                {!hideFareChoiceDetails && fareTypes && (
                  <FareTypesCarousel
                    fareTypes={fareTypes}
                    onFareTypeSelect={onFareTypeSelect}
                    scrollAnalytics={scrollAnalytics}
                  />
                )}
              </UitkCardAlohaContentSection>
            </UitkSpacing>
            {!hideFareChoiceDetails && (
              <>
                <UitkCardAlohaContentSection className="all-x-margin-four" padded={false}>
                  {baggageFeesInformation ? (
                    <BaggageFeesInformation {...baggageFeesInformation} />
                  ) : (
                    selectedFareType && <BaggageFeesInformation {...selectedFareType.baggageFeesInformation} />
                  )}
                </UitkCardAlohaContentSection>
                <UitkCardAlohaContentSection className="all-x-margin-four">
                  {changeCancellationMessages ? (
                    <ChangeCancellationMessages {...changeCancellationMessages} />
                  ) : (
                    selectedFareType && <ChangeCancellationMessages {...selectedFareType.changeCancellationMessages} />
                  )}
                </UitkCardAlohaContentSection>
              </>
            )}
          </>
        )}
        {
          // TODO: remove length condition based on https://jira.expedia.biz/browse/FODF-969
          hygieneAmenitiesPresentation?.airlineAmenityGroups?.[0]?.amenities?.length > 0 && (
            <HygieneAmenities hygieneAmenities={hygieneAmenitiesPresentation} />
          )
        }
        {flightsJourneyPolicies && (
          <UitkSpacing margin="three">
            <UitkCardAlohaContentSection>
              {flightsJourneyPolicies.messages.map((message: Messages, messageIndex: number) => (
                <UitkLayoutFlexItem key={`journey-policy-${messageIndex}`}>
                  <UitkText size={200} spacing="three">
                    <Phrase {...message} />
                  </UitkText>
                </UitkLayoutFlexItem>
              ))}
            </UitkCardAlohaContentSection>
          </UitkSpacing>
        )}
      </UitkCardAloha>
      {useOwnFooter && (
        <UitkSheet isVisible={true} className="s-t-margin-twelve s-t-padding-one">
          <DetailsAndFaresFooter
            openForcedChoiceModal={openForcedChoiceModal}
            onApplySelect={onApplySelect}
            handleOfferSelect={handleOfferSelect}
            availableFaresInformation={availableFaresInformation}
            isSmallViewport={isSmallViewport}
          />
        </UitkSheet>
      )}
    </>
  );
};
